/* Collection filter bar (brand book: FilterBar).
   Loads every book in the collection from ?view=ccs-data (250 a page), then filters and
   sorts in the page: FOC window (relative to now, 10 PM Eastern cutoffs), format, release
   FOC date, publisher. State lives in the URL (?foc=week&fmt=Omnibus,HC&fd=2026-11-02&pub=DC&sort=locks)
   so filtered views can be shared. Without JavaScript the server-rendered pages still work. */
(() => {
  const root = document.querySelector('[data-ccs-collection]');
  if (!root) return;

  const t = JSON.parse(root.querySelector('[data-i18n]')?.textContent || '{}');
  const bar = root.querySelector('[data-filters]');
  const grid = root.querySelector('[data-grid]');
  const none = root.querySelector('[data-none]');
  const more = root.querySelector('[data-more]');
  const pagination = root.querySelector('[data-pagination]');
  if (!bar || !grid) return;

  const PAGE = 24;
  const FORMAT_ORDER = ['Omnibus', 'Epic Collection', 'Library Ed.', 'Deluxe HC', 'HC', 'TPB', 'Compact', 'GN'];
  const DAY = 864e5;
  const params = new URLSearchParams(location.search);
  const state = {
    foc: params.get('foc') || '',
    fmt: (params.get('fmt') || '').split(',').filter(Boolean),
    fd: params.get('fd') || '',
    pub: params.get('pub') || '',
    sort: params.get('sort') || '',
  };
  let items = [];
  let shown = PAGE;

  const cutoff = (ymd) => (window.CCS?.FOCLock?.cutoff ? window.CCS.FOCLock.cutoff(ymd) : Date.parse(ymd + 'T22:00:00-05:00'));
  const todayNY = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());
  const focDateLabel = (ymd) => {
    const [y, m, d] = ymd.split('-').map(Number);
    const date = new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString(document.documentElement.lang || 'en', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    return (t.foc_date_option || 'FOC __DATE__').replace('__DATE__', date);
  };
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  const focWindow = (it) => {
    // Comic OS deletes comic_shop.foc_date once a book's cutoff passes (post-FOC revision),
    // and books are only listed once their FOC is announced: an unreleased pre-order with
    // no FOC date is past FOC.
    if (!it.foc) return it.pre && it.rel && it.rel >= todayNY() ? 'past' : '';
    const ms = it.lock - Date.now();
    if (ms > 0 && ms <= 7 * DAY) return 'week';
    if (ms > 0 && ms <= 30 * DAY) return '30';
    if (ms <= 0 && it.rel && it.rel >= todayNY()) return 'past';
    return '';
  };
  const inFoc = (it, v) => {
    const w = focWindow(it);
    return v === '30' ? w === 'week' || w === '30' : w === v;
  };

  const matches = (it, skip) =>
    (skip === 'foc' || !state.foc || inFoc(it, state.foc)) &&
    (skip === 'fmt' || !state.fmt.length || state.fmt.includes(it.fmt)) &&
    (skip === 'fd' || !state.fd || it.foc === state.fd) &&
    (skip === 'pub' || !state.pub || it.pub === state.pub);

  const sorters = {
    store: (a, b) => a.pos - b.pos,
    locks: (a, b) => {
      const now = Date.now();
      const ka = a.foc && a.lock > now ? a.lock : Infinity;
      const kb = b.foc && b.lock > now ? b.lock : Infinity;
      return ka - kb || (a.rel || '9').localeCompare(b.rel || '9') || a.pos - b.pos;
    },
    onsale: (a, b) => (a.rel || '9').localeCompare(b.rel || '9') || a.pos - b.pos,
    price_asc: (a, b) => a.price - b.price || a.pos - b.pos,
    price_desc: (a, b) => b.price - a.price || a.pos - b.pos,
    title: (a, b) => a.title.localeCompare(b.title),
    pages: (a, b) => (b.pp || 0) - (a.pp || 0) || a.pos - b.pos,
  };
  // This Week's FOC (data-mode="foc"): the prototype's bar, format chips + four sorts.
  const focMode = root.dataset.mode === 'foc';

  const chip = (group, value, label, pressed, count) =>
    `<button type="button" class="ccs-chip" data-group="${group}" data-value="${esc(value)}" aria-pressed="${pressed}"${count === 0 && !pressed ? ' disabled' : ''}>${esc(label)}${count != null ? ` <span class="ccs-mono">${count}</span>` : ''}</button>`;

  const renderFocBar = () => {
    const pool = items.filter((it) => matches(it, 'fmt'));
    const formats = FORMAT_ORDER.filter((f) => items.some((it) => it.fmt === f));
    const cur = state.fmt[0] || '';
    let html = `<span class="ccs-label">${esc(t.format)}</span>`;
    html += chip('fmt', '', t.all_formats, !cur, pool.length);
    for (const f of formats) html += chip('fmt', f, f, cur === f, pool.filter((it) => it.fmt === f).length);
    html += `<label class="ccs-foc-sort"><span class="ccs-label">${esc(t.sort)}</span><select data-select="sort">${[
      ['price_desc', t.sort_price_desc], ['onsale', t.sort_soonest], ['pages', t.sort_pages], ['title', t.sort_title],
    ].map(([v, label]) => `<option value="${v}"${state.sort === v ? ' selected' : ''}>${esc(label)}</option>`).join('')}</select></label>`;
    bar.innerHTML = html;
    return items.filter((it) => matches(it));
  };

  const renderBar = () => {
    if (focMode) return renderFocBar();
    const hasFoc = items.some((it) => it.foc && it.pre);
    const focBase = items.filter((it) => matches(it, 'foc'));
    const fmtBase = items.filter((it) => matches(it, 'fmt'));
    const fdBase = items.filter((it) => matches(it, 'fd'));
    const pubBase = items.filter((it) => matches(it, 'pub'));
    const formats = FORMAT_ORDER.filter((f) => items.some((it) => it.fmt === f));
    const focDates = [...new Set(items.filter((it) => it.foc && it.pre && it.lock > Date.now()).map((it) => it.foc))].sort();
    const pubs = [...new Set(items.map((it) => it.pub).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    const filtered = items.filter((it) => matches(it));
    const active = state.foc || state.fmt.length || state.fd || state.pub;
    const sortOpts = [
      ...(hasFoc ? [['locks', t.sort_locks]] : []),
      ['onsale', t.sort_onsale],
      ['store', t.sort_store],
      ['price_asc', t.sort_price_asc],
      ['price_desc', t.sort_price_desc],
      ['title', t.sort_title],
    ];

    let html = '';
    if (hasFoc) {
      html += `<div class="ccs-filters__group" role="group" aria-labelledby="FilterFoc"><span class="ccs-label" id="FilterFoc">${esc(t.foc)}</span>`;
      for (const [v, label] of [['week', t.foc_week], ['30', t.foc_30], ['past', t.foc_past]]) {
        html += chip('foc', v, label, state.foc === v, focBase.filter((it) => inFoc(it, v)).length);
      }
      html += '</div>';
    }
    if (formats.length > 1) {
      html += `<div class="ccs-filters__group" role="group" aria-labelledby="FilterFmt"><span class="ccs-label" id="FilterFmt">${esc(t.format)}</span>`;
      for (const f of formats) html += chip('fmt', f, f, state.fmt.includes(f), fmtBase.filter((it) => it.fmt === f).length);
      html += '</div>';
    }
    html += '<div class="ccs-filters__group ccs-filters__selects">';
    if (focDates.length) {
      html += `<label class="ccs-select"><span class="visually-hidden">${esc(t.foc_date)}</span><select data-select="fd"><option value="">${esc(t.any_foc_date)}</option>${focDates
        .map((d) => `<option value="${d}"${state.fd === d ? ' selected' : ''}>${esc(focDateLabel(d))} (${fdBase.filter((it) => it.foc === d).length})</option>`)
        .join('')}</select></label>`;
    }
    if (pubs.length > 1) {
      html += `<label class="ccs-select"><span class="visually-hidden">${esc(t.publisher)}</span><select data-select="pub"><option value="">${esc(t.any_publisher)}</option>${pubs
        .map((p) => `<option value="${esc(p)}"${state.pub === p ? ' selected' : ''}>${esc(p)} (${pubBase.filter((it) => it.pub === p).length})</option>`)
        .join('')}</select></label>`;
    }
    html += `<label class="ccs-select"><span class="visually-hidden">${esc(t.sort)}</span><select data-select="sort">${sortOpts
      .map(([v, label]) => `<option value="${v}"${state.sort === v ? ' selected' : ''}>${esc(t.sort)}: ${esc(label)}</option>`)
      .join('')}</select></label>`;
    html += '</div>';
    html += `<p class="ccs-filters__count" role="status" aria-live="polite">${filtered.length} ${esc(t.of)} ${items.length} ${esc(t.books)}${
      active ? ` · <button type="button" class="ccs-filters__clear" data-clear>${esc(t.clear)}</button>` : ''
    }</p>`;
    bar.innerHTML = html;
    return filtered;
  };

  const renderGrid = (filtered) => {
    const list = filtered.slice().sort(sorters[state.sort] || sorters.store);
    grid.innerHTML = list.slice(0, shown).map((it) => `<li>${it.html}</li>`).join('');
    none.hidden = list.length > 0;
    more.hidden = list.length <= shown;
    window.CCS?.FOCLock?.update(grid);
  };

  const syncUrl = () => {
    const p = new URLSearchParams();
    if (state.foc) p.set('foc', state.foc);
    if (state.fmt.length) p.set('fmt', state.fmt.join(','));
    if (state.fd) p.set('fd', state.fd);
    if (state.pub) p.set('pub', state.pub);
    if (state.sort && state.sort !== state.defaultSort) p.set('sort', state.sort);
    const q = p.toString();
    history.replaceState(null, '', location.pathname + (q ? '?' + q : ''));
  };

  const render = (resetPaging = true) => {
    if (resetPaging) shown = PAGE;
    renderGrid(renderBar());
    syncUrl();
    root.dispatchEvent(new CustomEvent('ccs:rendered', { detail: { state: { ...state }, items } }));
  };
  // Other controls (the FOC publisher picker) can set filters: ccs:filter {pub: 'DC'}.
  root.addEventListener('ccs:filter', (e) => {
    Object.assign(state, e.detail || {});
    if (focMode && e.detail && 'pub' in e.detail) state.fmt = [];
    if (items.length) render();
  });

  bar.addEventListener('click', (e) => {
    const clear = e.target.closest('[data-clear]');
    if (clear) {
      Object.assign(state, { foc: '', fmt: [], fd: '', pub: '' });
      return render();
    }
    const b = e.target.closest('.ccs-chip');
    if (!b) return;
    const { group, value } = b.dataset;
    if (group === 'foc') state.foc = state.foc === value ? '' : value;
    if (group === 'fmt' && focMode) state.fmt = value ? [value] : [];
    else if (group === 'fmt') state.fmt = state.fmt.includes(value) ? state.fmt.filter((f) => f !== value) : [...state.fmt, value];
    render();
    bar.querySelector(`.ccs-chip[data-group="${group}"][data-value="${CSS.escape(value)}"]`)?.focus();
  });
  bar.addEventListener('change', (e) => {
    const s = e.target.closest('[data-select]');
    if (!s) return;
    state[s.dataset.select] = s.value;
    render();
    bar.querySelector(`[data-select="${s.dataset.select}"]`)?.focus();
  });
  more.querySelector('button')?.addEventListener('click', () => {
    shown += PAGE;
    renderGrid(items.filter((it) => matches(it)));
  });

  const load = async () => {
    const url = (page) => `${root.dataset.url}?view=ccs-data&page=${page}`;
    const first = await fetch(url(1)).then((r) => r.json());
    const rest = await Promise.all(Array.from({ length: first.pages - 1 }, (_, i) => fetch(url(i + 2)).then((r) => r.json())));
    items = [first, ...rest].flatMap((p) => p.items).map((it) => ({ ...it, lock: it.foc ? cutoff(it.foc) : NaN }));
    const hasFoc = items.some((it) => it.foc && it.pre);
    state.defaultSort = focMode ? 'price_desc' : hasFoc ? 'locks' : 'store';
    if (!state.sort || !sorters[state.sort]) state.sort = state.defaultSort;
    bar.hidden = false;
    if (pagination) pagination.hidden = true;
    render(false);
  };

  load().catch(() => {
    /* Keep the server-rendered page and its pagination if the feed fails. */
  });
})();
